import classNames from 'classnames';
import styled from 'styled-components';

import { Vertical } from 'atoms/layout/flex';
import { LabelL, LabelS } from 'atoms/typography';
import { useIntlContext } from 'contexts/intl';
import { FixtureState } from 'lib/so5';
import { laptopAndAbove, tabletAndAbove } from 'style/mediaQuery';

const Wrapper = styled(Vertical)`
  height: 100%;
`;

const DateWrapper = styled(Vertical)`
  &.small {
    margin-top: var(--half-unit);
  }

  &.big {
    flex-direction: row;
    gap: var(--half-unit);

    @media ${laptopAndAbove} {
      gap: var(--unit);
    }
  }
`;

const DateRangeLabel = styled(LabelL)`
  white-space: nowrap;
  color: var(--c-white);

  &.closed {
    color: var(--c-nd-400);
  }

  &.selected {
    color: var(--c-black);
  }

  &.big {
    --typography-font: var(--t-headline-m);

    @media ${tabletAndAbove} {
      --typography-font: var(--t-headline-xl);
    }
  }
`;

const MonthLabel = styled(LabelS)`
  text-transform: uppercase;

  &.big {
    @media ${tabletAndAbove} {
      --typography-font: var(--t-headline-xs);
    }
  }
`;

const getHumanReadableDay = (date: Date) => {
  const newDate = new Date(date);
  // before 5am UTC (1am NYC, 7am Paris time), it's still the previous day
  if (newDate.getUTCHours() < 5) {
    newDate.setUTCDate(newDate.getUTCDate() - 1);
  }
  return newDate.toISOString().slice(0, 10); // yyyy-mm-dd
};

type Props = {
  isDaily: boolean;
  endDate: Nullable<Date>;
  variant: 'small' | 'big';
  startDate: Nullable<Date>;
  gameWeek: Nullable<string>;
  selected: Nullable<boolean>;
  fixtureState: Nullable<FixtureState>;
};

export const FixturePickerContent = ({
  startDate,
  endDate,
  selected,
  variant,
  isDaily,
  gameWeek,
  fixtureState,
}: Props) => {
  const { formatDate } = useIntlContext();
  if (!startDate || !endDate) {
    return null;
  }

  return (
    <Wrapper className={variant} center>
      {variant === 'big' && gameWeek && (
        <LabelS as="p" bold color="var(--c-nd-400-opaque)">
          {isDaily
            ? formatDate(getHumanReadableDay(startDate), {
                weekday: 'long',
                timeZone: 'UTC',
              })
            : gameWeek}
        </LabelS>
      )}
      <DateWrapper center className={variant}>
        <MonthLabel
          as="p"
          bold
          className={variant}
          color={selected ? 'var(--c-nl-500)' : 'var(--c-nd-300)'}
        >
          {isDaily ? (
            <>
              {formatDate(getHumanReadableDay(startDate), {
                month: 'short',
                timeZone: 'UTC',
              })}
            </>
          ) : (
            <>
              {formatDate(startDate, {
                month: 'short',
              })}
            </>
          )}
        </MonthLabel>
        <DateRangeLabel
          as="p"
          bold
          className={classNames(variant, {
            selected,
            closed: fixtureState === FixtureState.closed,
          })}
        >
          {isDaily ? (
            <>
              {formatDate(getHumanReadableDay(startDate), {
                day: 'numeric',
                timeZone: 'UTC',
              })}
            </>
          ) : (
            <>
              {formatDate(startDate, { day: 'numeric' })}-
              {formatDate(endDate, { day: 'numeric' })}
            </>
          )}
        </DateRangeLabel>
      </DateWrapper>
    </Wrapper>
  );
};
